import React, { FC, useRef, useState, useCallback } from 'react'
import { motion } from 'framer-motion'

interface IMagneticScrollButtonProps {
  text?: string
  targetId?: string
  onClick?: () => void
  className?: string
}

const MagneticScrollButton: FC<IMagneticScrollButtonProps> = ({
  text = 'explore our work',
  targetId = 'about mamf',
  onClick,
  className = '',
}) => {
  const containerRef = useRef<HTMLButtonElement>(null)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isHovered, setIsHovered] = useState(false)

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (!containerRef.current) return
    if (typeof window !== 'undefined' && (!window.matchMedia('(hover: hover) and (pointer: fine)').matches || window.innerWidth < 800)) {
      return
    }
    const { left, top, width, height } = containerRef.current.getBoundingClientRect()
    const centerX = left + width / 2
    const centerY = top + height / 2
    const distanceX = e.clientX - centerX
    const distanceY = e.clientY - centerY

    // Balanced, smooth magnetic pull factor
    setPosition({
      x: distanceX * 0.22,
      y: distanceY * 0.22,
    })
  }, [])

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    setPosition({ x: 0, y: 0 })
  }

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      onClick()
      return
    }

    e.preventDefault()

    // 1. Try resolving target element by id
    const cleanId = targetId.replace(/^#/, '')
    const targetElement =
      document.getElementById(cleanId) ||
      document.querySelector(`[data-name="${cleanId}"]`) ||
      document.querySelector('section:nth-of-type(2)')

    // @ts-ignore
    if (window.lenis && targetElement) {
      // @ts-ignore
      window.lenis.scrollTo(targetElement, { duration: 1.5, offset: 0 })
    } else if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth' })
    } else {
      window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })
    }
  }

  // Format circular text
  const formattedText = text.trim().toUpperCase()

  return (
    <button
      ref={containerRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`group relative flex items-center justify-center cursor-pointer select-none bg-transparent border-0 p-0 focus:outline-none ${className}`}
      aria-label={text}
    >
      <motion.div
        animate={{ x: position.x, y: position.y }}
        transition={{
          type: 'spring',
          stiffness: 150,
          damping: 20,
          mass: 0.5,
        }}
        className="relative w-32 h-32 md:w-36 md:h-36 lg:w-36 lg:h-36 xl:w-40 xl:h-40 flex items-center justify-center"
      >
        {/* Rotating Circular Text */}
        <motion.div
          animate={{ rotate: 360 }}
          transition={{
            repeat: Infinity,
            duration: isHovered ? 8 : 16,
            ease: 'linear',
          }}
          className="absolute inset-0 w-full h-full origin-center"
        >
          <svg
            viewBox="0 0 100 100"
            className="w-full h-full overflow-visible pointer-events-none select-none text-on-surface-background fill-current"
          >
            <defs>
              <path
                id="circlePathHero"
                d="M 50, 50 m -36, 0 a 36, 36 0 1, 1 72, 0 a 36, 36 0 1, 1 -72, 0"
                fill="none"
              />
            </defs>
            {/* Top Text (25% center) */}
            <text
              fontSize="7.5"
              className="fill-current font-palanquin font-bold uppercase"
              textAnchor="middle"
            >
              <textPath
                xlinkHref="#circlePathHero"
                href="#circlePathHero"
                startOffset="25%"
                textLength="80"
                lengthAdjust="spacing"
              >
                {formattedText}
              </textPath>
            </text>
            {/* Bottom Text (75% center) */}
            <text
              fontSize="7.5"
              className="fill-current font-palanquin font-bold uppercase"
              textAnchor="middle"
            >
              <textPath
                xlinkHref="#circlePathHero"
                href="#circlePathHero"
                startOffset="75%"
                textLength="80"
                lengthAdjust="spacing"
              >
                {formattedText}
              </textPath>
            </text>
            {/* Left Asterisk Separator (0% center) */}
            <text
              fontSize="8"
              className="fill-current font-palanquin font-bold"
              textAnchor="middle"
            >
              <textPath xlinkHref="#circlePathHero" href="#circlePathHero" startOffset="0%">
                *
              </textPath>
            </text>
            {/* Right Asterisk Separator (50% center) */}
            <text
              fontSize="8"
              className="fill-current font-palanquin font-bold"
              textAnchor="middle"
            >
              <textPath xlinkHref="#circlePathHero" href="#circlePathHero" startOffset="50%">
                *
              </textPath>
            </text>
          </svg>
        </motion.div>

        {/* Centered Arrow (with vertical bounce animation) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-primary dark:text-on-surface-background transition-colors duration-300">
          <motion.div
            animate={{
              y: isHovered ? [0, 5, 0] : [0, 3, 0],
            }}
            transition={{
              repeat: Infinity,
              duration: isHovered ? 0.8 : 1.6,
              ease: 'easeInOut',
            }}
            className="flex items-center justify-center"
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="transform group-hover:scale-110 transition-transform duration-300"
            >
              <path d="M12 4v16M5 13l7 7 7-7" />
            </svg>
          </motion.div>
        </div>
      </motion.div>
    </button>
  )
}

export default MagneticScrollButton
